import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  AlertTitle,
  Chip,
  Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BusinessIcon from '@mui/icons-material/Business';
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import BugReportIcon from '@mui/icons-material/BugReport';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ShieldIcon from '@mui/icons-material/Shield';
import MenuBookIcon from '@mui/icons-material/MenuBook';

import { patchesAPI, jobsAPI, emailLogsAPI, customersAPI, environmentsAPI, wafAPI, backupsAPI, kbAPI, uptimeAPI } from '../services/api';
import { usePermissions } from '../context/PermissionsContext';

function StatCard({ title, value, icon, color, loading, subtitle, onClick }) {
  return (
    <Card onClick={onClick} sx={onClick ? { cursor: 'pointer', '&:hover': { boxShadow: 4 } } : undefined}>
      <CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Box
            sx={{
              backgroundColor: `${color}.light`,
              borderRadius: 1.5,
              p: 0.5,
              mr: 1,
              display: 'flex',
              '& .MuiSvgIcon-root': { fontSize: 18 },
            }}
          >
            {icon}
          </Box>
          <Typography variant="subtitle2" component="div" sx={{ lineHeight: 1.25 }}>
            {title}
          </Typography>
        </Box>
        {loading ? (
          <CircularProgress size={18} />
        ) : (
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            {value}
          </Typography>
        )}
        {subtitle && !loading && (
          <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
        )}
      </CardContent>
    </Card>
  );
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// Customers whose uptime this month is at risk of missing the 99.9% commitment, or already has. Nothing here may break the dashboard.
export function UptimeWarning() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    const now = new Date();
    uptimeAPI.summary(now.getUTCFullYear(), now.getUTCMonth() + 1).then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data || !data.customers.length) return null;
  const month = `${MONTH_NAMES[data.month - 1]} ${data.year}`;
  const worst = (c) => (c.status === 'missed' ? 2 : 1) * 1000 + (c.counted_minutes || 0);
  const risky = data.customers.filter((c) => c.status === 'missed' || c.status === 'at_risk').sort((a, b) => worst(b) - worst(a));
  const down = data.customers.filter((c) => c.now === 'down');
  const missed = risky.filter((c) => c.status === 'missed').length;
  const open = <Button color="inherit" size="small" onClick={() => navigate('/uptime')}>Open Customer Uptime</Button>;

  if (!risky.length && !down.length) {
    return (
      <Alert severity="success" sx={{ mb: 2 }} action={open}>
        Customer uptime, {month}: all {data.customers.length} customers are inside their downtime allowance for the month.
      </Alert>
    );
  }
  return (
    <Alert severity={missed ? 'error' : 'warning'} sx={{ mb: 2, alignItems: 'flex-start' }} action={open}>
      <AlertTitle>Customer uptime, {month}: {missed ? `${missed} customer${missed === 1 ? ' has' : 's have'} missed 99.9%` : `${risky.length} customer${risky.length === 1 ? ' is' : 's are'} at risk of missing 99.9%`}</AlertTitle>
      {risky.slice(0, 8).map((c) => (
        <Typography key={c.customer_id} variant="body2">
          <strong>{c.code}</strong> {c.name}: {c.status === 'missed' ? 'over the allowance' : 'at risk'}, {c.counted_minutes} of {c.budget_minutes} allowed minutes used ({c.uptime_percent == null ? '-' : `${c.uptime_percent.toFixed(3)}%`} so far){c.now === 'down' ? ', down right now' : ''}
        </Typography>
      ))}
      {risky.length > 8 && <Typography variant="body2">and {risky.length - 8} more.</Typography>}
      {down.filter((c) => !risky.includes(c)).length > 0 && (
        <Typography variant="body2" sx={{ mt: 0.5 }}>Down right now: {down.filter((c) => !risky.includes(c)).map((c) => c.code).join(', ')}.</Typography>
      )}
    </Alert>
  );
}

const REVIEW_TEXT = { open: 'Not started', in_review: 'In review', review_complete: 'Review complete, awaiting approval', approved: 'Approved' };

function fmtDue(deadline) {
  if (!deadline) return '';
  const tz = deadline.timezone;
  const opts = { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', hour12: false };
  try { return new Date(deadline.due_at).toLocaleString('en-GB', tz ? { ...opts, timeZone: tz } : opts) + (tz ? ` (${tz})` : ''); }
  catch (e) { return new Date(deadline.due_at).toLocaleString(); }
}

// Reminder for the weekly database backup review: what is waiting, what is late, and what is assigned to me.
function BackupReminder() {
  const { isAdmin, canReviewBackups } = usePermissions();
  const navigate = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => {
    backupsAPI.dashboard().then((r) => setD(r.data)).catch(() => setD(null));
  }, []);

  if (!d) return null;
  const mine = d.my_open_actions || [];
  if (!(isAdmin() || canReviewBackups() || mine.length > 0)) return null;

  const state = d.deadline?.state;
  const severity = d.overdue.length > 0 || state === 'overdue' ? 'error'
    : state === 'approved' ? 'success'
    : state === 'due_soon' ? 'warning'
    : mine.length > 0 ? 'info' : 'info';
  const total = d.reported.length + d.missing.length;

  return (
    <Alert
      severity={severity}
      sx={{ mb: 4, alignItems: 'flex-start' }}
      action={<Button color="inherit" size="small" onClick={() => navigate('/backups')}>Open backup reports</Button>}
    >
      <AlertTitle>Backup review: week of {String(d.week_start).slice(0, 10)}</AlertTitle>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
        <Chip size="small" label={REVIEW_TEXT[d.status] || d.status} />
        {d.deadline && (
          <Chip
            size="small"
            color={state === 'overdue' ? 'error' : state === 'due_soon' ? 'warning' : state === 'approved' ? 'success' : 'default'}
            variant={state === 'on_track' ? 'outlined' : 'filled'}
            label={state === 'approved' ? 'Approved before the deadline' : `${state === 'overdue' ? 'Overdue, was due' : 'Due'} ${fmtDue(d.deadline)}`}
          />
        )}
      </Box>
      {!d.has_data && <Typography variant="body2">No backup reports have arrived this week yet. They normally arrive on Monday morning.</Typography>}
      {d.has_data && d.missing.length > 0 && (
        <Typography variant="body2">Waiting for {d.missing.length} of {total} instances to report: {d.missing.join(', ')}.</Typography>
      )}
      {d.has_data && d.missing.length === 0 && d.status !== 'approved' && (
        <Typography variant="body2">All {total} instances have reported{d.status === 'open' ? ', so the review can start.' : '.'}</Typography>
      )}
      {d.has_data && d.exceptions > 0 && d.status !== 'approved' && (
        <Typography variant="body2">{d.exceptions} exceptions{d.uncovered > 0 ? `, ${d.uncovered} still without an action` : ', all with an action'}.</Typography>
      )}
      {d.open_actions > 0 && <Typography variant="body2">{d.open_actions} open action{d.open_actions === 1 ? '' : 's'} must be completed before approval.</Typography>}
      {d.status === 'approved' && d.approved_by && <Typography variant="body2">Approved by {d.approved_by}.</Typography>}
      {d.overdue.map((o) => (
        <Typography key={o.week_start} variant="body2" sx={{ fontWeight: 600 }}>
          {o.no_reports
            ? `Overdue: no backup reports were received for the week of ${String(o.week_start).slice(0, 10)}.`
            : `Overdue: week of ${String(o.week_start).slice(0, 10)} was not approved by its deadline${o.missing && o.missing.length ? `. No report from ${o.missing.join(', ')}` : ''}.`}
        </Typography>
      ))}
      {mine.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>Assigned to you ({mine.length}):</Typography>
          {mine.slice(0, 5).map((a) => (
            <Typography key={a.id} variant="body2">
              • {a.database_name || a.instance_name}{a.backup_type ? ` (${a.backup_type})` : ''}, week of {String(a.week_start).slice(0, 10)}: {a.description}
            </Typography>
          ))}
        </Box>
      )}
    </Alert>
  );
}

function Dashboard() {
  const { isAdmin, canEditKb } = usePermissions();
  const navigate = useNavigate();
  const [kb, setKb] = useState(null);
  useEffect(() => {
    kbAPI.stats().then((r) => setKb(r.data)).catch(() => setKb(null));   // a failure here must never break the dashboard
  }, []);
  const [stats, setStats] = useState({
    totalPatches: 0,
    draftPatches: 0,
    activeJobs: 0,
    emailsSent: 0,
    activeCustomers: 0,
    limsProduction: 0,
    limsTest: 0,
    limsDevelopment: 0,
    sdmsProduction: 0,
    sdmsTest: 0,
    sdmsDevelopment: 0,
    wafBlocks: 0,
  });
  const [wafPeriod, setWafPeriod] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [patches, jobs, emailStats, customers, environmentStats] = await Promise.all([
        patchesAPI.list(),
        jobsAPI.list(),
        emailLogsAPI.stats(),
        customersAPI.list(),
        environmentsAPI.stats(),
      ]);

      // WAF stats are optional: a failure here should not blank the dashboard
      let waf = { blocked_ips: 0, period: '' };
      try {
        waf = (await wafAPI.stats()).data;
      } catch (e) {
        console.error('Failed to load WAF stats:', e);
      }
      setWafPeriod(waf.period || '');

      const draftCount = patches.data.filter(p => p.status === 'draft').length;
      const activeJobCount = jobs.data.filter(j => j.is_active).length;
      const activeCustomerCount = customers.data.filter(c => c.is_active).length;

      setStats({
        totalPatches: patches.data.length,
        draftPatches: draftCount,
        activeJobs: activeJobCount,
        // the server returns one row per email type and result for the last 30 days; add up the ones that were sent
        emailsSent: (Array.isArray(emailStats.data) ? emailStats.data : []).filter((r) => r.status === 'sent').reduce((n, r) => n + Number(r.count), 0),
        activeCustomers: activeCustomerCount,
        limsProduction: environmentStats.data.lims_production || 0,
        limsTest: environmentStats.data.lims_test || 0,
        limsDevelopment: environmentStats.data.lims_development || 0,
        sdmsProduction: environmentStats.data.sdms_production || 0,
        sdmsTest: environmentStats.data.sdms_test || 0,
        sdmsDevelopment: environmentStats.data.sdms_development || 0,
        wafBlocks: waf.blocked_ips || 0,
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
        Welcome to the StarLIMS Management Portal
      </Typography>

      <UptimeWarning />
      <BackupReminder />

      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
          <StatCard
            title="Active Customers"
            value={stats.activeCustomers}
            icon={<BusinessIcon color="primary" />}
            color="primary"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
          <StatCard
            title="Total Patches"
            value={stats.totalPatches}
            icon={<NewReleasesIcon color="primary" />}
            color="primary"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
          <StatCard
            title="Draft Patches"
            value={stats.draftPatches}
            icon={<NewReleasesIcon color="warning" />}
            color="warning"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
          <StatCard
            title="Active Jobs"
            value={stats.activeJobs}
            icon={<ScheduleIcon color="success" />}
            color="success"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
          <StatCard
            title="Emails Sent (30 days)"
            value={stats.emailsSent}
            icon={<CheckCircleIcon color="info" />}
            color="info"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
          <StatCard
            title="LIMS Production"
            value={stats.limsProduction}
            icon={<RocketLaunchIcon color="success" />}
            color="success"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
          <StatCard
            title="LIMS Test"
            value={stats.limsTest}
            icon={<BugReportIcon color="warning" />}
            color="warning"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
          <StatCard
            title="LIMS Development"
            value={stats.limsDevelopment}
            icon={<DeveloperModeIcon color="info" />}
            color="info"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
          <StatCard
            title="SDMS Production"
            value={stats.sdmsProduction}
            icon={<RocketLaunchIcon color="success" />}
            color="success"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
          <StatCard
            title="SDMS Test"
            value={stats.sdmsTest}
            icon={<BugReportIcon color="warning" />}
            color="warning"
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
          <StatCard
            title="SDMS Development"
            value={stats.sdmsDevelopment}
            icon={<DeveloperModeIcon color="info" />}
            color="info"
            loading={loading}
          />
        </Grid>
        {isAdmin() && (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
            <StatCard
              title={`WAF Blocks${wafPeriod ? ` (${wafPeriod})` : ''}`}
              value={stats.wafBlocks}
              icon={<ShieldIcon color="error" />}
              color="error"
              loading={loading}
            />
          </Grid>
        )}
        {kb && (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
            <StatCard
              title="Knowledge Base Articles"
              value={kb.published}
              subtitle={`${kb.updated_30d} updated in the last 30 days`}
              icon={<MenuBookIcon color="primary" />}
              color="primary"
              onClick={() => navigate('/kb')}
            />
          </Grid>
        )}
        {kb && canEditKb() && kb.review_due > 0 && (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
            <StatCard
              title="KB Due for Review"
              value={kb.review_due}
              subtitle="Click to see which"
              icon={<MenuBookIcon color="warning" />}
              color="warning"
              onClick={() => navigate('/kb?view=review')}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default Dashboard;
